import Vuex, {Module, ModuleOptions, Store} from 'vuex';
import {VueConstructor} from 'vue';
import VuexPersistence from 'vuex-persist';
import {AnimationModule} from './animation.store';
import {ElementConfigModule} from './config';
import {SessionModule} from './session';
import {CustomerProfileModule} from './customerProfile';
import {DeepLinkModule} from './deepLink';
import {FeaturedItemModule} from './featuredItem';
import {ItemFavoriteModule} from './itemFavorite';
import {CurbsideLocationsModule, LocationModule} from './location';
import {LocationDiscountModule} from './locationDiscount';
import {LocationHoursModule} from './locationHours';
import {MenuModule} from './menu';
import {ILineItem, ITicket, OrderModule, TicketsModule} from './order';
import {PaymentAccountModule} from './paymentAccount';
import {RecentItemModule} from './recentItem';
import {SimpleMenuModule} from './simpleMenu';
import {SimpleTicketModule} from './simpleTicket';
import {SpecialsModule} from './specials';
import {TrendingItemModule} from './trendingItem';
import {UIModule} from './ui';
import {VehiclesModule} from './vehicles';
import {vuexHook} from './shim';
import {HistoryModule} from './history/history.store';
import {ContainerTransitionMappingModule} from './containerTransitionMapping/containerTransitionMapping.store';
import {ItemService} from '../services';
import {CampaignModule} from './campaign';

export const setupStore = (Vue: VueConstructor, VuexInstance: typeof Vuex) => {
	Vue.use(VuexInstance);

	const persist = new VuexPersistence<any>({
		key: 'customerPortal',
		reducer: (state) => {
			const reducedOrderLineItems: ILineItem[] = ItemService.lineItemsForLocalStorage(state.order.lineItems);
			const reducedActiveTickets = state.tickets_v2.activeTickets.map(
				(ticket: ITicket) => {
					return {
						...ticket,
						lineItems: []
					};
				}
			);
			const reducedLatestActiveTicket = state.tickets_v2.latestTicketInternal ? {
				...state.tickets_v2.latestTicketInternal,
				lineItems: []
			} : null;

			return {
				customerProfile: state.customerProfile,
				locations: state.locations,
				order: {
					...state.order,
					lineItems: reducedOrderLineItems,
					cartAnimationQueue: [],
					workingTicket: null
				},
				session: state.session,
				specials: state.specials,
				tickets: state.tickets,
				tickets_v2: {...state.tickets_v2, activeTickets: reducedActiveTickets, latestTicketInternal: reducedLatestActiveTicket}
			};
		},
		modules: [
			'customerProfile',
			'locations',
			'order',
			'session',
			'specials',
			'tickets', // SimpleTicketStore*
			'tickets_v2', // TicketsStore
		],
	});

	// *We can't reuse the 'tickets' name until we are reinitializing
	//  stores with new releases of Customer Portal.


	const dynamicRegisterPlugin = (store: Store<any>) => {
		// Override dynamic module handling in vuex
		let origRegisterModule: any;
		if(store.registerModule) {
			origRegisterModule = store.registerModule.bind(store);
			// your module interfaces
			type T = any;
			// eslint-disable-next-line
			// @ts-ignore
			store.registerModule = (
				path: string,
				module: Module<T, any>,
				options?: ModuleOptions
			) => {
				// eslint-disable-next-line @typescript-eslint/ban-types,no-prototype-builtins
				if((store.state as Object).hasOwnProperty(path)) {
					origRegisterModule(path, module, {
						preserveState: true
					});
				} else {
					origRegisterModule(path, module, options);
				}
			};
		}
	};

	return new VuexInstance.Store<any>({
		modules: {
			// Make sure the key is the same as the "name" you put in the decorator
			animation: AnimationModule,
			elementConfig: ElementConfigModule,
			session: SessionModule,
			customerProfile: CustomerProfileModule,
			curbsideLocations: CurbsideLocationsModule,
			deepLink: DeepLinkModule,
			featuredItems: FeaturedItemModule,
			itemFavorites: ItemFavoriteModule,
			locations: LocationModule,
			locationDiscounts: LocationDiscountModule,
			locationHours: LocationHoursModule,
			fullMenu: MenuModule,
			order: OrderModule,
			tickets_v2: TicketsModule,
			paymentAccount: PaymentAccountModule,
			recentItems: RecentItemModule,
			simpleMenu: SimpleMenuModule,
			tickets: SimpleTicketModule,
			specials: SpecialsModule,
			trendingItems: TrendingItemModule,
			ui: UIModule,
			vehicles: VehiclesModule,
			history: HistoryModule,
			containerTransitionMapping: ContainerTransitionMappingModule,
			campaigns: CampaignModule,
		},
		plugins: [
			vuexHook,
			dynamicRegisterPlugin,
			persist.plugin
		]
	});
};

